/**
 * A person, by NAME — the principal a read records (who proposed, approved, recorded, acted, was
 * assigned, decided), named from their profile (usability audit B6, DEC-97 extended by DEC-111;
 * bata-platform#508).
 *
 * The reads that record who did something carried the token subject alone, so a screen could only
 * print an id («Account abcd1234»). The platform now publishes a paired name beside each subject —
 * `authorName`, `approvedByNames` (aligned index by index with `approvedBy`, `null` where one
 * approver has no name), `closedByName`, `recordedByName`, `actorName`, `assigneeName`,
 * `actor_name` — from `user_profile.display_name`, one lookup per response.
 *
 * **Never invented.** The platform's own principal (`platform`, `platform:<job>`) is never looked
 * up and never named: it reads «Sistema», never its token as if it were a person. A person with no
 * profile, or no name on it, has a `null` name, and the screen falls back to what it showed before —
 * the id, or the caller's own «te» / a short account reference where the screen already said so.
 *
 * **Why a name is absent (ADR 2127, bata-platform 0.84).** Beside every name the reads now publish
 * `<name>_absent_reason` — `machine`, `service_account` or `no_profile`, `null` where a name stands
 * (`approvedByNamesAbsentReasons` aligned with `approvedBy`). The screen says it in words — «Sistema»,
 * «Servizio automatico», «Utente senza nome nel profilo» — and keeps the principal behind the «id»
 * button (`IdTag`) instead of printing an account reference or a raw `svc.*` principal. A reason this
 * build does not know falls back to what the screen showed before.
 *
 * Shared because three features show people: Management (change sets, parameters, modules, rules,
 * plans, alerts), and the in-season cycle band and execution pack.
 */

import { useTranslation } from 'react-i18next';

import type { ExportCell, ExportColumn } from '../export';
import type { DataColumn } from './data-table';
import { AbsentWords } from '../primitives';
import { IdTag } from './run-words';

/** The platform's own principal as its write paths spell it (bata-platform `actor_is_machine`). */
export const MACHINE_PRINCIPAL = 'platform';

/** Whether a recorded principal is the platform's rather than a person's. Pure. */
export function isMachinePrincipal(subject: string | null | undefined): boolean {
  if (subject === null || subject === undefined) return false;
  return subject === MACHINE_PRINCIPAL || subject.startsWith(`${MACHINE_PRINCIPAL}:`);
}

/** Why a read publishes no name beside a principal (ADR 2127): the platform's, a service's, or a person with no profile name. */
export type PersonAbsentReason = 'machine' | 'service_account' | 'no_profile';

/** The reasons that name a principal who is not the platform itself. */
export type PersonReason = Exclude<PersonAbsentReason, 'machine'>;

/** The words for a principal with no published name — literal keys, so the coverage check sees all three. */
export function personReasonText(t: Translate, reason: PersonAbsentReason): string {
  if (reason === 'machine') return t('person.system');
  return reason === 'service_account' ? t('person.serviceAccount') : t('person.noProfile');
}

/** Narrows a read's `<name>_absent_reason` to the three it publishes; anything else is `null`. Pure. */
export function personAbsentReason(value: string | null | undefined): PersonAbsentReason | null {
  return value === 'machine' || value === 'service_account' || value === 'no_profile' ? value : null;
}

/** The code a name cell carries for such a reason, so the workbook can say it in words. */
const REASON_CODE: Readonly<Record<PersonReason, string>> = {
  service_account: '__service_account__',
  no_profile: '__no_profile__',
};

/** A recorded principal, classified for display. */
export type PersonLabel =
  | { readonly kind: 'machine' }
  | { readonly kind: 'named'; readonly name: string; readonly id: string }
  /** No name, and the read says why (`service_account` | `no_profile`): the id goes behind the «id» button. */
  | { readonly kind: 'reasoned'; readonly reason: PersonReason; readonly id: string }
  /** No name on record: the screen shows its fallback for `id`. */
  | { readonly kind: 'unnamed'; readonly id: string }
  | { readonly kind: 'none' };

/**
 * «Sistema» for the platform, the published name, the reason no name is published, else the id to
 * fall back on. `reason` is the read's `<name>_absent_reason`; one this build does not know is ignored.
 * Pure.
 */
export function personLabel(
  subject: string | null | undefined, name: string | null | undefined, reason?: string | null,
): PersonLabel {
  if (isMachinePrincipal(subject)) return { kind: 'machine' };
  const id = subject?.trim() ?? '';
  const published = name?.trim() ?? '';
  if (published !== '' && id !== '') return { kind: 'named', name: published, id };
  if (id === '') return { kind: 'none' };
  if (reason === 'machine') return { kind: 'machine' };
  if (reason === 'service_account' || reason === 'no_profile') return { kind: 'reasoned', reason, id };
  return { kind: 'unnamed', id };
}

/** The key «Sistema» reads from. */
export const SYSTEM_PERSON_KEY = 'person.system';

type Translate = (key: string) => string;

/**
 * A principal as text: «Sistema», the name, else `fallback(id)` — by default the id itself.
 * `''` for no principal at all. Pure.
 */
export function personText(
  t: Translate, subject: string | null | undefined, name: string | null | undefined,
  fallback: (id: string) => string = (id) => id, reason?: string | null,
): string {
  const label = personLabel(subject, name, reason);
  if (label.kind === 'machine') return t(SYSTEM_PERSON_KEY);
  if (label.kind === 'named') return label.name;
  if (label.kind === 'reasoned') return personReasonText(t, label.reason);
  return label.kind === 'unnamed' ? fallback(label.id) : '';
}

/**
 * Approvers as text, each by name where the platform has one: `names` is aligned index by index
 * with `subjects` (bata-platform's `approvedByNames`); a missing or `null` entry falls back, or says
 * its `reasons` entry (`approvedByNamesAbsentReasons`, aligned the same way). Pure.
 */
export function peopleTexts(
  t: Translate, subjects: readonly string[] | null | undefined, names: readonly (string | null)[] | null | undefined,
  fallback: (id: string) => string = (id) => id, reasons?: readonly (string | null)[] | null,
): string[] {
  return (subjects ?? []).map((subject, index) => personText(t, subject, names?.[index] ?? null, fallback, reasons?.[index] ?? null));
}

/**
 * A principal inline: «Sistema»; the name, its id in the tooltip; else the id in mono (or the
 * caller's `fallback` text, the whole id in its tooltip when the text is not the id). With the read's
 * `reason` and no name: the reason in words, the id behind the «id» button.
 */
export function PersonName({ subject, name, fallback, reason }: {
  readonly subject: string | null | undefined;
  readonly name: string | null | undefined;
  readonly fallback?: (id: string) => string;
  readonly reason?: string | null | undefined;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const label = personLabel(subject, name, reason);
  if (label.kind === 'reasoned') {
    return (
      <span className="inline-flex items-baseline gap-1" data-person-reason={label.reason} data-person-of={label.id}>
        <span>{personReasonText(t, label.reason)}</span>
        <IdTag id={label.id} kind={t('person.idKind')} />
      </span>
    );
  }
  if (label.kind === 'machine') return <span data-person="machine" title={subject ?? undefined}>{t(SYSTEM_PERSON_KEY)}</span>;
  if (label.kind === 'named') return <span data-person-of={label.id} title={label.id}>{label.name}</span>;
  if (label.kind === 'none') return null;
  const text = fallback === undefined ? label.id : fallback(label.id);
  // A fallback that shortens the id («account d9ba9aa2») keeps the whole id in the tooltip.
  return (
    <span
      className={fallback === undefined ? 'font-numeric' : undefined}
      data-person-unnamed={label.id}
      title={text === label.id ? undefined : label.id}
    >
      {text}
    </span>
  );
}

/**
 * The «id» button, after a space, for a principal shown in words because the read says why it has no
 * name — nothing for a named person, the platform or a principal with no reason. For a sentence the
 * words stand in (`personText`), so the id is still one press away.
 */
export function PersonIdTag({ subject, name, reason }: {
  readonly subject: string | null | undefined;
  readonly name: string | null | undefined;
  readonly reason?: string | null | undefined;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const label = personLabel(subject, name, reason);
  // The space before the button is its own: nothing, not even a trailing space, where there is no button.
  return label.kind === 'reasoned' ? <>{' '}<IdTag id={label.id} kind={t('person.idKind')} /></> : null;
}

/** `PersonIdTag` for each of several principals (`names` and `reasons` aligned index by index). */
export function PeopleIdTags({ subjects, names, reasons }: {
  readonly subjects: readonly string[] | null | undefined;
  readonly names?: readonly (string | null)[] | null | undefined;
  readonly reasons?: readonly (string | null)[] | null | undefined;
}): React.JSX.Element {
  return (
    <>
      {(subjects ?? []).map((subject, index) => (
        <PersonIdTag key={`${subject}-${index}`} subject={subject} name={names?.[index] ?? null} reason={reasons?.[index] ?? null} />
      ))}
    </>
  );
}

// ── the same people in a file ────────────────────────────────────────────────

/** The code a machine principal's name cell carries, so the workbook can say «Sistema». */
const SYSTEM_CODE = '__system__';

/**
 * The workbook's words for a name cell: «Sistema» for the platform, the name (or id) itself
 * otherwise. The CSV keeps the cell's value — the platform's own token for a machine.
 */
export const PERSON_NAME_DISPLAY: NonNullable<ExportColumn['display']> = (code, translate) => {
  if (code === SYSTEM_CODE) return translate(SYSTEM_PERSON_KEY);
  for (const reason of Object.keys(REASON_CODE) as PersonReason[]) {
    if (code === REASON_CODE[reason]) return personReasonText(translate, reason);
  }
  return code;
};

/**
 * One principal's NAME cell for a file: the profile name; the id where no name is on record (as
 * the screen falls back); the platform as «Sistema» in the workbook and its token in the CSV; not
 * reported when nobody is recorded. The id itself goes in its own (technical) column. Where the read
 * gives the reason no name is published, the workbook says it («Servizio automatico»); the CSV keeps
 * the id. Pure.
 */
export function personExportCell(
  subject: string | null | undefined, name: string | null | undefined, reason?: string | null,
): ExportCell {
  const label = personLabel(subject, name, reason);
  if (label.kind === 'machine') return { kind: 'label', value: subject ?? MACHINE_PRINCIPAL, code: SYSTEM_CODE };
  if (label.kind === 'named') return { kind: 'label', value: label.name, code: label.name };
  if (label.kind === 'reasoned') return { kind: 'label', value: label.id, code: REASON_CODE[label.reason] };
  if (label.kind === 'unnamed') return { kind: 'label', value: label.id, code: label.id };
  return { kind: 'missing', state: 'not-reported' };
}

/**
 * Several principals' names in one cell, «; » between them, each by name where the platform has
 * one (`names` aligned index by index with `subjects`), else by id; not reported when there are
 * none. Pure.
 */
export function peopleExportCell(
  subjects: readonly string[] | null | undefined, names: readonly (string | null)[] | null | undefined,
): ExportCell {
  const list = subjects ?? [];
  if (list.length === 0) return { kind: 'missing', state: 'not-reported' };
  return {
    kind: 'text',
    value: list.map((subject, index) => {
      const label = personLabel(subject, names?.[index] ?? null);
      return label.kind === 'named' ? label.name : subject;
    }).join('; '),
  };
}

// ── who decided a queue line ─────────────────────────────────────────────────

/**
 * Who decided a queue line — bata-ui issue 218 Phase 3, bata-platform ADR 9021 (proposals,
 * price steps, transfers, alerts) and ADR 5001 (launch proposals), `docs/adr/9026`.
 *
 * The decision reads publish the principal of the line's FIRST recorded decision
 * (`disposition_actor` / `dispositionActor`) and its profile name (`…_actor_name` /
 * `…ActorName`). Both are `null` while the line is pending or superseded: nobody has decided
 * it, so the cell is ABSENT with that reason — said once under the table (`AbsenceNotes`) —
 * never blank and never a name. A decided line whose read names no principal is absent with
 * its own reason. A named one is the person by name («System» for the platform's principal,
 * the id where no profile name is on record: `PersonName`).
 */
export interface DecidedBy {
  /** The line's recorded decision; `null` while pending. */
  readonly disposition: string | null | undefined;
  readonly actor: string | null | undefined;
  readonly actorName: string | null | undefined;
  /** The read's `…actor_name_absent_reason` (ADR 2127); `null`/absent where a name stands. */
  readonly actorNameAbsentReason?: string | null | undefined;
}

/** The disposition a newer run gives a line it took over: history, not a decision. */
const SUPERSEDED_DISPOSITION = 'superseded';

export const DECIDED_BY_PENDING_KEY = 'person.decidedBy.absent.pending';
export const DECIDED_BY_SUPERSEDED_KEY = 'person.decidedBy.absent.superseded';
export const DECIDED_BY_NOT_RECORDED_KEY = 'person.decidedBy.absent.notRecorded';

/** Why the decider is absent, as a catalogue key; `null` when a principal is published. Pure. */
export function decidedByAbsentKey({ disposition, actor }: DecidedBy): string | null {
  if (disposition === null || disposition === undefined || disposition === '') return DECIDED_BY_PENDING_KEY;
  if (disposition === SUPERSEDED_DISPOSITION) return DECIDED_BY_SUPERSEDED_KEY;
  return actor === null || actor === undefined || actor.trim() === '' ? DECIDED_BY_NOT_RECORDED_KEY : null;
}

/** The decider as words for a column's sort and filter value; `null` when absent. Pure. */
export function decidedByText(t: Translate, line: DecidedBy): string | null {
  if (decidedByAbsentKey(line) !== null) return null;
  const text = personText(t, line.actor, line.actorName, undefined, line.actorNameAbsentReason);
  return text === '' ? null : text;
}

/** The decider for a file: the name cell `personExportCell` writes, absent while undecided. Pure. */
export function decidedByExportCell(line: DecidedBy): ExportCell {
  if (decidedByAbsentKey(line) !== null) return { kind: 'missing', state: 'not-ranged' };
  return personExportCell(line.actor, line.actorName, line.actorNameAbsentReason);
}

/**
 * The «Decided by» cell: the person, or «Nobody decided» in words with its reason registered once
 * with the table (`AbsenceNotes`, which every `DataTable` is); a badge outside one (ADR 9061).
 */
export function DecidedByCell({ className = 'py-1', ...line }: DecidedBy & { readonly className?: string }): React.JSX.Element {
  const reasonKey = decidedByAbsentKey(line);
  if (reasonKey === null) {
    return <td className={className} data-decided-by={line.actor ?? ''}><PersonName subject={line.actor} name={line.actorName} reason={line.actorNameAbsentReason} /></td>;
  }
  return (
    <td className={`${className} text-ink-secondary`} data-decided-by="">
      <AbsentWords absent={{ labelKey: 'person.decidedBy.absent.label', reasonKey }} />
    </td>
  );
}

/**
 * The «Decided by» column of a decision table: the cell above, sorted and filtered on the
 * decider's words (absent sorts last), explained by one shared guide sentence.
 */
export function decidedByColumn<T>(
  t: Translate, of: (row: T) => DecidedBy, options: { readonly className?: string; readonly headerClassName?: string } = {},
): DataColumn<T> {
  return {
    id: 'decided_by', headerKey: 'person.decidedBy.column', descriptionKey: 'person.decidedBy.help', kind: 'enum',
    ...(options.headerClassName === undefined ? {} : { headerClassName: options.headerClassName }),
    value: (row) => decidedByText(t, of(row)),
    cell: (row) => <DecidedByCell {...of(row)} {...(options.className === undefined ? {} : { className: options.className })} />,
  };
}
